import * as React from 'react';
import { ArchetypeSpecificStepRunSummaryPanelProps } from '.';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import {
    ChecklistItem,
    ChecklistItemComment,
    ChecklistItemStatus,
    ChecklistItemStatusType,
    OrgUser,
    RegressionStatus,
    ReleaseStepRegressionTestingData,
    ReleaseStepType,
    TestRun,
    UserGroup,
} from '@/types';
import { statusColorForRegressionStatus } from '@/helpers';
import { regressionStatusDisplayString } from '@/components/StepContentRegressionTesting';
import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import Tag from 'antd/lib/tag';
import { StepRun } from '@/types/stepRun';
import Space from 'antd/lib/space';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { Avatar, Divider, Popover, Progress, Table, Tooltip } from 'antd/lib';
import {
    displayTextForChecklistItemStatusType,
    displayTextForUserGroup,
    displayUserNameShort,
} from '@/helpers/displayHelpers';
import {
    ArrowRightOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    CloseCircleFilled,
    MessageFilled,
    MessageOutlined,
    MinusCircleFilled,
    StopFilled,
} from '@ant-design/icons';
import { selectOrgGroups, selectOrgUsers } from '@/selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { OwnerAvatar, OwnerAvatarGroup } from '@/components/Avatars';
import { Nullable } from '@/typescriptHelpers';
import { dayjs } from '@/extendedDayjs';
import { createStepRunRoute } from '@/helpers/routeHelpers';
import { Link } from 'react-router-dom';
import SmallStatusNode from '@/components/SmallStatusNode';
import CIAppStoreBuildComponent from '@/components/CIAppStoreBuildComponent';

export const RegressionTestingStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({
    stepRun,
    orgId,
    appId,
    releaseVersion,
    shouldDisplayCompactVersion,
}) => {
    const stepData: ReleaseStepRegressionTestingData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    return (
        <Flex vertical gap={20} style={{ padding: '2px 2px 0px 2px' }}>
            <Flex vertical gap={10}>
                <Text style={{ ...appHomeCardsWidgetTitleStyle }}>Regression Testing Status</Text>
                <Flex
                    align="center"
                    gap={12}
                    style={{
                        backgroundColor: antGrey.grey2,
                        padding: '16px 20px',
                        borderRadius: 8,
                    }}
                >
                    {createRegressionTestingTopBar(stepRun)}
                </Flex>
            </Flex>
            <RegressionTestingTestCasesProgressBar testRuns={stepData.testRuns} compact={shouldDisplayCompactVersion} />
            <RegressionOrApprovalItemsProgressBar
                items={stepData.testItems}
                stepArchetype={ReleaseStepType.RegressionTesting}
                compact={shouldDisplayCompactVersion}
            />
            <RegressionOrApprovalItemsSummaryTable
                items={stepData?.testItems}
                orgId={orgId}
                appId={appId}
                releaseVersion={releaseVersion}
                stepRunId={stepRun.id}
                stepArchetype={ReleaseStepType.RegressionTesting}
            />
        </Flex>
    );
};

export interface RegressionStatusTagProps {
    status: RegressionStatus;
    isReleaseFromScraper: boolean;
}

export const RegressionStatusTag: React.FC<RegressionStatusTagProps> = ({ status, isReleaseFromScraper }) => {
    const color = statusColorForRegressionStatus(status, isReleaseFromScraper);
    const statusText = regressionStatusDisplayString(status, isReleaseFromScraper);

    return (
        <Tag
            style={{
                ...titleStyle,
                fontSize: 10,
                lineHeight: '22px',
                margin: 0,
                color: color,
                border: `1px solid ${color}`,
            }}
        >
            <div
                style={{
                    height: '100%',
                    width: '100%',
                    textAlign: 'center',
                    position: 'relative',
                    top: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    justifyItems: 'center',
                    alignItems: 'stretch',
                    gap: 8,
                }}
            >
                {statusText}
            </div>
        </Tag>
    );
};

export const createRegressionTestingTopBar = (stepRun: StepRun): React.ReactNode => {
    const stepData: ReleaseStepRegressionTestingData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    const regressionStatus = stepData.status;
    const regressionBuildInfo = stepData.regressionBuildInfo;

    const hasRegressionBuildInfo = !!regressionBuildInfo;

    return (
        <>
            <RegressionStatusTag status={regressionStatus} isReleaseFromScraper={false} />
            {hasRegressionBuildInfo && (
                <Flex align="center" gap={8} style={{ height: 22 }}>
                    <CIAppStoreBuildComponent
                        ciBuildIdentifier={regressionBuildInfo?.ciBuild?.buildIdentifier}
                        ciBuildIntegrationId={regressionBuildInfo?.ciBuild.integrationId}
                        appStoreBuildIdentifier={regressionBuildInfo?.associatedAppStoreBuild?.buildNumber}
                        appStoreBuildIntegrationId={regressionBuildInfo?.associatedAppStoreBuild?.integrationId}
                        isAppStoreBuildProcessed={regressionBuildInfo?.associatedAppStoreBuild?.isProcessed}
                        strong={false}
                    />
                    <Text type="secondary" style={{ fontSize: 14 }}>
                        (active build)
                    </Text>
                </Flex>
            )}
        </>
    );
};

const checklistItemStatusCellIcon = (status: ChecklistItemStatus): React.ReactNode => {
    switch (status?.status) {
        case ChecklistItemStatusType.Approved:
        case ChecklistItemStatusType.Passed:
            return <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />;
        case ChecklistItemStatusType.Rejected:
        case ChecklistItemStatusType.Failed:
            return <CloseCircleFilled style={{ color: runwayColors.statusRed }} />;
        case ChecklistItemStatusType.InProgress:
            return <ClockCircleFilled style={{ color: runwayColors.statusYellow }} />;
        case ChecklistItemStatusType.Blocked:
            return <StopFilled style={{ color: antGrey.grey7 }} />;
        default:
            return <MinusCircleFilled style={{ color: antGrey.grey5 }} />;
    }
};

export interface RegressionTestingTestCasesProgressBarProps {
    testRuns: TestRun[];
    compact: boolean;
}

const RegressionTestingTestCasesProgressBar: React.FC<RegressionTestingTestCasesProgressBarProps> = ({
    testRuns,
    compact,
}) => {
    const haveAnyTestRuns = testRuns.length > 0;
    const latestTestRun = testRuns[0];

    const testCaseStatuses = latestTestRun?.statuses ?? [];
    const haveAnyTestCaseStatuses = testCaseStatuses.length > 0;

    const { name, integrationId, completedAt, completionFraction } = latestTestRun ?? {};

    const [icon, alt] = iconAndAltForIntegrationType(integrationId);

    return haveAnyTestRuns && haveAnyTestCaseStatuses ?
            <Flex vertical gap={10} style={{ width: compact ? '100%' : '60%' }}>
                <Flex align="center" justify="space-between">
                    <Flex align="center" gap={8}>
                        <Text style={{ ...appHomeCardsWidgetTitleStyle }}>{alt}</Text>
                        <Divider type="vertical" style={{ margin: 0 }} />
                        <Avatar src={icon} alt={alt} size={18} shape="square" style={{ border: 'none' }} />
                        <Text style={{ ...appHomeCardsWidgetTitleStyle }}>
                            Run {testRuns.length} ({name})
                        </Text>
                        <SmallStatusNode
                            color={completedAt ? runwayColors.statusGreen : runwayColors.statusYellow}
                            size={6}
                            tooltip={completedAt ? 'Completed' : 'Active'}
                        />
                    </Flex>
                    <Text style={{ ...appHomeCardsWidgetTitleStyle }}>{`${completionFraction * 100}% tested`}</Text>
                </Flex>
                <Flex style={{ width: '100%', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                    {testCaseStatuses.map((status, index) => (
                        <Tooltip key={index} title={`${status.count} cases ${status.displayableName}`}>
                            <div
                                key={index}
                                style={{ flexBasis: `${status.fraction * 100}%`, backgroundColor: `#${status.color}` }}
                            />
                        </Tooltip>
                    ))}
                </Flex>
            </Flex>
        :   null;
};

export interface RegressionOrApprovalItemsProgressBarProps {
    items: Nullable<ChecklistItem[]>;
    stepArchetype: ReleaseStepType.RegressionTesting | ReleaseStepType.Approvals;
    compact: boolean;
}

export const RegressionOrApprovalItemsProgressBar: React.FC<RegressionOrApprovalItemsProgressBarProps> = ({
    items,
    stepArchetype,
    compact,
}) => {
    const completionStatusType =
        stepArchetype === ReleaseStepType.RegressionTesting ?
            ChecklistItemStatusType.Passed
        :   ChecklistItemStatusType.Approved;

    const totalItems = items?.length ?? 0;
    const haveAnyItems = totalItems > 0;
    const completedItems =
        haveAnyItems ? items?.filter((item) => item.status?.status === completionStatusType).length : 0;

    const progressPercent = (completedItems / totalItems) * 100;
    const progressColor =
        haveAnyItems && totalItems === completedItems ? runwayColors.statusGreen : runwayColors.statusYellow;

    return haveAnyItems ?
            <Flex vertical gap={10} style={{ width: compact ? '100%' : '60%' }}>
                <Flex align="center" justify="space-between">
                    <Text style={{ ...appHomeCardsWidgetTitleStyle }}>
                        {stepArchetype === ReleaseStepType.RegressionTesting ?
                            'Regression testing progress (items)'
                        :   'Approvals progress'}
                    </Text>
                    <Text style={{ ...appHomeCardsWidgetTitleStyle }}>{`${progressPercent.toFixed(0)}% complete`}</Text>
                </Flex>
                <Tooltip
                    title={`${progressPercent.toFixed(0)}% - ${completedItems} of ${totalItems} items ${stepArchetype === ReleaseStepType.RegressionTesting ? 'completed' : 'approved'}, ${totalItems - completedItems} items pending`}
                >
                    <Progress
                        percent={progressPercent}
                        strokeColor={progressColor}
                        trailColor={antGrey.grey4}
                        showInfo={false}
                        className="widget-progress-bar"
                    />
                </Tooltip>
            </Flex>
        :   null;
};

export interface RegressionOrApprovalItemsSummaryTableProps {
    items: Nullable<ChecklistItem[]>;
    orgId: string;
    appId: string;
    releaseVersion: string;
    stepRunId: string;
    stepArchetype: ReleaseStepType.RegressionTesting | ReleaseStepType.Approvals;
}

export const RegressionOrApprovalItemsSummaryTable: React.FC<RegressionOrApprovalItemsSummaryTableProps> = ({
    items,
    orgId,
    appId,
    releaseVersion,
    stepRunId,
    stepArchetype,
}) => {
    const completionStatusType =
        stepArchetype === ReleaseStepType.RegressionTesting ?
            ChecklistItemStatusType.Passed
        :   ChecklistItemStatusType.Approved;

    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, orgId));
    const orgGroups = useSelector((state: RootState) => selectOrgGroups(state, orgId));

    const pendingItems = React.useMemo(() => {
        return items?.filter((item) => item.status?.status !== completionStatusType) ?? [];
    }, [items]);

    const cliUsersOrApproverGroupsByTestItemId: Record<string, [Nullable<OrgUser[]>, Nullable<UserGroup[]>]> =
        React.useMemo(() => {
            const result: Record<string, [OrgUser[], UserGroup[]]> = {};

            pendingItems?.forEach((item) => {
                const cliUsers = item.owners
                    .map((owner) => orgUsers.find((user) => user.id === owner))
                    .filter((u): u is OrgUser => u !== null);
                const approverGroups = item.approverGroups.map((groupId) => orgGroups[groupId]);
                result[item.id] = [cliUsers as OrgUser[], approverGroups as UserGroup[]];
            });

            return result;
        }, [pendingItems, orgUsers, orgGroups]);

    return pendingItems?.length > 0 ?
            <Flex vertical gap={10}>
                <Text style={{ ...appHomeCardsWidgetTitleStyle }}>
                    {stepArchetype === ReleaseStepType.RegressionTesting ?
                        'Pending Testing Items'
                    :   'Pending Approvals'}
                </Text>
                <div style={{ borderRadius: 8, overflow: 'hidden' }}>
                    <Table
                        dataSource={pendingItems.length > 5 ? pendingItems.slice(0, 5) : pendingItems}
                        columns={[
                            {
                                dataIndex: 'title',
                                key: 'title',
                                render: (text: string) => (
                                    <Text style={{ fontSize: 14, color: antGrey.grey8, fontWeight: 500 }}>{text}</Text>
                                ),
                                width: '65%',
                                onCell: (_) => {
                                    return {
                                        style: {
                                            padding: '12px 16px',
                                        },
                                    };
                                },
                            },
                            {
                                key: 'owners',
                                render: (_, item: ChecklistItem) => {
                                    const [cliUsers, approverGroups] = cliUsersOrApproverGroupsByTestItemId[item.id];

                                    return (
                                        <ChecklistItemOwnersCellContent
                                            userOwners={cliUsers}
                                            approverGroups={approverGroups}
                                        />
                                    );
                                },
                                width: '20%',
                                onCell: (_) => {
                                    return {
                                        style: {
                                            padding: '0px 16px',
                                            textAlign: 'right',
                                        },
                                    };
                                },
                            },
                            {
                                dataIndex: 'status',
                                key: 'status',
                                render: (status: ChecklistItemStatus) => (
                                    <ChecklistItemStatusCellContent status={status} />
                                ),
                                width: '20%',
                                onCell: (_) => {
                                    return {
                                        style: {
                                            padding: '5px 18px 5px 14px',
                                            backgroundColor: antGrey.grey3,
                                        },
                                    };
                                },
                            },
                            {
                                dataIndex: 'comment',
                                key: 'comment',
                                render: (comment: ChecklistItemComment) => {
                                    return (
                                        <ChecklistItemCommentCellContent
                                            comment={comment}
                                            commentCreatedByUser={
                                                orgUsers?.find(
                                                    (user) => user.id === comment?.createdBy,
                                                ) as Nullable<OrgUser>
                                            }
                                        />
                                    );
                                },
                                width: '5%',
                                onCell: (_) => {
                                    return {
                                        style: {
                                            padding: '0px 16px',
                                            textAlign: 'center',
                                        },
                                    };
                                },
                            },
                        ]}
                        pagination={false}
                        rowKey={(item) => item.id}
                        onRow={(_) => {
                            return {
                                style: {
                                    backgroundColor: antGrey.grey2,
                                    padding: '4px 0px',
                                    borderRadius: 8,
                                },
                            };
                        }}
                        showHeader={false}
                    />
                </div>
                <Flex align="center" justify="flex-end" gap={8} style={{ height: '40px' }}>
                    <Text style={{ fontSize: 12, color: antGrey.grey7, fontWeight: 400, lineHeight: '20px' }}>
                        Showing {pendingItems.length > 5 ? 5 : pendingItems.length} of {pendingItems.length} total
                        pending{' '}
                        {stepArchetype === ReleaseStepType.RegressionTesting ?
                            'regression testing items'
                        :   'approval items'}
                    </Text>
                    <Divider type="vertical" style={{ margin: 0 }} />
                    <Link to={createStepRunRoute(orgId, appId, releaseVersion, stepRunId)}>
                        <Flex align="center" gap={8}>
                            <Text style={{ fontSize: 12, color: runwayColors.primary }}>View all</Text>
                            <Avatar
                                size={18}
                                shape="circle"
                                src={<ArrowRightOutlined style={{ fontSize: 12, color: runwayColors.primary }} />}
                                style={{ border: 'none' }}
                            />
                        </Flex>
                    </Link>
                </Flex>
            </Flex>
        :   null;
};
export interface ChecklistItemOwnersCellContentProps {
    userOwners: Nullable<OrgUser[]>;
    approverGroups: Nullable<UserGroup[]>;
}

export const ChecklistItemOwnersCellContent: React.FC<ChecklistItemOwnersCellContentProps> = ({
    userOwners,
    approverGroups,
}) => {
    const hasAnyUserOwners = userOwners?.length > 0;
    const hasMultipleUserOwners = userOwners?.length > 1;

    const hasAnyApproverGroups = approverGroups?.length > 0;

    return (
        <Space align="center" size={8}>
            {hasAnyUserOwners && hasMultipleUserOwners ?
                <OwnerAvatarGroup maxAvatars={3} owners={userOwners as OrgUser[]} isDisabled={false} />
            : hasAnyUserOwners ?
                <OwnerAvatar user={userOwners[0] as OrgUser} isDisabled={false} />
            : hasAnyApproverGroups ?
                <Text
                    style={{
                        color: antGrey.grey8,
                        fontSize: 14,
                        fontWeight: 400,
                        textWrap: 'nowrap',
                    }}
                >
                    {approverGroups.map((group) => displayTextForUserGroup(group)).join(', ')}
                </Text>
            :   <></>}
        </Space>
    );
};

export interface ChecklistItemStatusCellContentProps {
    status: ChecklistItemStatus;
}

export const ChecklistItemStatusCellContent: React.FC<ChecklistItemStatusCellContentProps> = ({ status }) => {
    const statusText =
        displayTextForChecklistItemStatusType(status?.status as ChecklistItemStatusType) ?? 'Not started';

    return (
        <Space align="center" size={14}>
            <Avatar
                size={14}
                shape="circle"
                style={{
                    border: 'none',
                }}
                src={checklistItemStatusCellIcon(status)}
            />
            <Text
                style={{
                    fontSize: 10,
                    color: antGrey.grey8,
                    fontWeight: '600',
                    textAlign: 'end',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    textWrap: 'nowrap',
                }}
            >
                {statusText}
            </Text>
        </Space>
    );
};

export interface ChecklistItemCommentCellContentProps {
    comment: Nullable<ChecklistItemComment>;
    commentCreatedByUser: Nullable<OrgUser>;
}

export const ChecklistItemCommentCellContent: React.FC<ChecklistItemCommentCellContentProps> = ({
    comment,
    commentCreatedByUser,
}) => {
    const now = dayjs();

    const gotCommentCreatedByUser = !!commentCreatedByUser;

    const commentCreatedAt =
        comment?.createdAt && dayjs(comment?.createdAt).isValid() ? dayjs(comment?.createdAt) : null;
    const gotCommentCreatedAt = !!commentCreatedAt;

    return (
        <Space align="center" size={8} style={{ margin: 0 }}>
            {comment?.comment ?
                <Popover
                    content={
                        <Flex
                            gap={4}
                            align="stretch"
                            style={{
                                maxWidth: 400,
                            }}
                        >
                            <Text
                                style={{
                                    backgroundColor: antGrey.grey3,
                                    color: antGrey.grey8,
                                    borderRadius: 4,
                                    padding: '8px 12px',
                                    flex: 2,
                                }}
                            >
                                {comment?.comment}
                            </Text>
                            <Flex
                                vertical
                                style={{
                                    alignSelf: 'start',
                                    padding: '0px 12px 0px 12px',
                                    height: '100%',
                                }}
                            >
                                {gotCommentCreatedByUser && (
                                    <Space align="center" size={8}>
                                        <OwnerAvatar user={commentCreatedByUser} isDisabled={false} />
                                        <Text
                                            style={{
                                                color: antGrey.grey8,
                                                fontSize: 14,
                                            }}
                                        >
                                            {displayUserNameShort(commentCreatedByUser)}
                                        </Text>
                                    </Space>
                                )}
                                {gotCommentCreatedAt && (
                                    <Text
                                        style={{
                                            color: antGrey.grey7,
                                            marginTop: gotCommentCreatedByUser ? 8 : 2,
                                        }}
                                    >
                                        {dayjs.duration(now.diff(dayjs(commentCreatedAt))).humanize()} ago
                                    </Text>
                                )}
                            </Flex>
                        </Flex>
                    }
                    autoAdjustOverflow
                >
                    <MessageFilled style={{ color: antGrey.grey7 }} size={16} />
                </Popover>
            :   <MessageOutlined style={{ color: antGrey.grey7 }} size={16} />}
        </Space>
    );
};
